import { getApiOrigin } from '../../config/apiBase';
import { request } from '../../utils/request';
import { getVideoStoragePrefix } from '../../config/video';
// import { Task } from './types';

/**
 * 获取字幕列表
 * 接口：GET /video/subtitles/list
 * @param taskId 任务ID（视频ID，即 /video/task/get 接口返回的 id 值）
 * @returns Promise<Task.GetSubtitlesListResponse>
 */
export const getSubtitlesListApi = async (taskId: number): Promise<Task.GetSubtitlesListResponse> => {
  try {
    const response = await request<Task.GetSubtitlesListResponse>(`/video/subtitles/list`, {
      method: 'GET',
      params: { taskId }
    });
    
    if (response.code !== 0) {
      throw new Error(response.msg || '获取字幕列表失败');
    }
    
    return response;
  } catch (error: any) {
    const errorInfo = {
      'API调用失败': `/video/subtitles/list`,
      '任务ID': taskId,
      '状态码': error?.status || 'N/A',
      '状态文本': error?.statusText || 'N/A',
      '服务器响应': error?.response || 'N/A',
      '错误消息': error?.message || 'N/A',
      '请求URL': `${getApiOrigin()}/video/subtitles/list?taskId=${taskId}`,
      '请求方法': 'GET'
    };
    
    console.error('=== 获取字幕列表API调用错误详情 ===');
    console.table(errorInfo);
    console.error('完整错误对象:', error);
    
    throw error;
  }
};

/**
 * 获取任务状态
 * 接口：GET /video/task/get
 * @param taskId 任务ID（视频ID）
 * @returns Promise<Task.GetTaskStatusResult>
 */ 
export const getTaskStatusApi = async (taskId: number): Promise<Task.GetTaskStatusResult> => {
  try {
    // 调用新接口：/video/task/get（前缀为 VITE_API_URL）
    const response = await request<Task.GetTaskStatusResponse>(`/video/task/get`, {
      method: 'GET',
      params: { id: taskId }
    });
    
    if (response.code !== 0 || !response.data) {
      throw new Error(response.msg || '获取任务状态失败');
    }
    
    const taskData = response.data;
    // 使用存储资源前缀（非缓存资源，如视频、字幕等）
    const storagePrefix = getVideoStoragePrefix();
    
    // 处理媒体资源URL，添加代理前缀（非缓存资源使用 /storage）
    const processMediaUrl = (url: string | null | undefined): string | null => {
      if (!url) return null;
      // 如果已经是完整URL，直接返回
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
      }
      
      // ✅ 新增：如果URL已经包含代理前缀（/storage 或 /media），直接返回，避免重复拼接
      if (url.startsWith('/storage/') || url.startsWith('/media/')) {
        return url;
      }
      
      // 如果前缀是相对路径（如 /storage），确保路径拼接正确
      if (storagePrefix.startsWith('/')) {
        // 前缀是相对路径，确保 url 以 / 开头
        const cleanUrl = url.startsWith('/') ? url : `/${url}`;
        return `${storagePrefix}${cleanUrl}`;
      } else {
        // 前缀是绝对路径（兼容旧配置），确保路径以 / 开头
        const cleanUrl = url.startsWith('/') ? url : `/${url}`;
        return `${storagePrefix}${cleanUrl}`;
      }
    };
    
    // 转换响应数据为兼容格式（驼峰命名 + 下划线命名兼容）
    const videoHlsUrl = processMediaUrl(taskData.videoHlsUrl);
    const pureVideoHlsUrl = processMediaUrl(taskData.pureVideoHlsUrl);
    const vocalsHlsUrl = processMediaUrl(taskData.vocalsHlsUrl);
    const backgroudAudioHlsUrl = processMediaUrl(taskData.backgroudAudioHlsUrl);
    const pureVideoUrl = processMediaUrl(taskData.pureVideoUrl) || '';
    const resultUrl = processMediaUrl(taskData.resultUrl);
    const fileUrl = processMediaUrl(taskData.fileUrl) || '';
    const subtitleUrl = processMediaUrl(taskData.subtitleUrl);
    const scriptUrl = processMediaUrl(taskData.scriptUrl);
    
    const result: Task.GetTaskStatusResult = {
      // 驼峰命名（新格式）
      id: taskData.id,
      name: taskData.name,
      status: taskData.status,
      coverUrl: taskData.coverUrl,
      tag: taskData.tag,
      fileUrl: fileUrl,
      subtitleUrl: subtitleUrl,
      videoHlsUrl: videoHlsUrl,
      pureVideoHlsUrl: pureVideoHlsUrl,
      vocalsHlsUrl: vocalsHlsUrl,
      backgroudAudioHlsUrl: backgroudAudioHlsUrl,
      hasUserSrt: taskData.hasUserSrt,
      scriptUrl: scriptUrl,
      hasUserScript: taskData.hasUserScript,
      episodeIndex: taskData.episodeIndex,
      langCode: taskData.langCode,
      pureVideoUrl: pureVideoUrl,
      resultUrl: resultUrl,
      duration: taskData.duration,
      descript: taskData.descript,
      createTime: taskData.createTime,
      updateTime: taskData.updateTime,
      disableBgm: taskData.disableBgm,
      volumeGainDb: taskData.volumeGainDb ?? null,
      subtitleExtractType: taskData.subtitleExtractType ?? null,
      ocrBbox: taskData.ocrBbox ?? null,
      errorMessage: taskData.errorMessage ?? null,
      currentStepCode: taskData.currentStepCode ?? null,
      currentStepName: taskData.currentStepName ?? null,
      currentStepStatus: taskData.currentStepStatus ?? null,
      currentStepProgress: taskData.currentStepProgress ?? null,
      subtitleBurnRequest: taskData.subtitleBurnRequest ?? null,
      subtitleLineNumber: taskData.subtitleLineNumber ?? null,
      dubbingMethod: taskData.dubbingMethod ?? null,
      // 下划线命名（兼容旧代码）
      pure_video_hls_url: pureVideoHlsUrl,
      video_hls_url: videoHlsUrl,
      vocals_hls_url: vocalsHlsUrl,
      backgroud_audio_hls_url: backgroudAudioHlsUrl,
      pure_video_url: pureVideoUrl,
      result_url: resultUrl,
      file_url: fileUrl,
      subtitle_url: subtitleUrl,
      script_url: scriptUrl,
      has_user_srt: taskData.hasUserSrt,
      has_user_script: taskData.hasUserScript,
      disable_bgm: taskData.disableBgm,
      create_time: taskData.createTime,
      update_time: taskData.updateTime,
      episode_index: taskData.episodeIndex,
      lang_code: taskData.langCode,
      cover_url: taskData.coverUrl,
      // 注意：subtitles 字段已独立为单独接口，不再包含在此响应中
    } as any; // 使用 as any 以支持同时存在驼峰和下划线字段
    
    return result;
  } catch (error: any) {
    // 构建控制台错误信息
    const errorInfo = {
      'API调用失败': `/video/task/get`,
      '任务ID': taskId,
      '状态码': error?.status || 'N/A',
      '状态文本': error?.statusText || 'N/A',
      '服务器响应': error?.response || 'N/A',
      '错误消息': error?.message || 'N/A',
      '请求URL': `${getApiOrigin()}/video/task/get?id=${taskId}`,
      '请求方法': 'GET'
    };
    
    console.error('=== API调用错误详情 ===');
    console.table(errorInfo);
    console.error('完整错误对象:', error);
    
    // 直接抛出错误，不再返回模拟数据
    throw error;
  }
};

/**
 * 修改字幕行数
 * 接口：PUT /video/task/update-subtitle-line-number
 */
export const updateSubtitleLineNumberApi = (
  body: Task.UpdateSubtitleLineNumberPayload
): Promise<Task.UpdateSubtitleLineNumberResponse> => {
  return request<Task.UpdateSubtitleLineNumberResponse>('/video/task/update-subtitle-line-number', {
    method: 'PUT',
    body,
  });
};

/**
 * 合并任务生成视频
 * @param taskId 任务ID
 * @returns Promise<Task.MergeTaskResult>
 */
export const mergeTaskApi = async (taskId: number): Promise<Task.MergeTaskResult> => {
  try {
    console.log('调用API: /task/merge/', taskId);
    const response = await request<Task.MergeTaskResult>(`/task/merge/${taskId}`, {
      method: 'POST'
    });
    console.log('合并任务API响应:', response);
    
    return response;
  } catch (error: any) {
    // 构建控制台错误信息
    const errorInfo = {
      'API调用失败': `/task/merge/${taskId}`,
      '任务ID': taskId,
      '状态码': error?.status || 'N/A',
      '状态文本': error?.statusText || 'N/A',
      '服务器响应': error?.response || 'N/A',
      '错误消息': error?.message || 'N/A',
      '请求URL': `${getApiOrigin()}/task/merge/${taskId}`,
      '请求方法': 'POST'
    };
    
    console.error('=== 合并任务API调用错误详情 ===');
    console.table(errorInfo);
    console.error('完整错误对象:', error);
    
    // 直接抛出错误
    throw error;
  }
};

/**
 * 获取纯净版视频列表
 * 接口：GET /video/task/source-list
 * @param seriesId 剧集ID
 * @param sort 排序规则：1-按创建时间倒序（可选，默认为1）
 * @param name 搜索关键词（可选）
 * @returns Promise<Task.GetPureVideoListResponse> 纯净版视频列表
 */
export const getPureVideoListApi = (seriesId: number, sort: number = 1, name?: string): Promise<Task.GetPureVideoListResponse> => {
  // GET 请求使用 query 参数（params），form-data 参数会作为 URL 查询参数传递
  const params: { seriesId: number; sort: number; name?: string } = {
    seriesId,
    sort,
  };
  if (name && name.trim()) {
    params.name = name.trim();
  }
  return request<Task.GetPureVideoListResponse>('/video/task/source-list', {
    method: 'GET',
    params,
  });
};

/**
 * 获取多语种视频列表
 * 接口：GET /video/task/list
 * @param seriesId 剧集ID
 * @param langCode 语言代码
 * @param sort 排序规则：1-按创建时间倒序，2-按名称升序（可选，默认为1）
 * @param name 搜索关键词（可选）
 * @returns Promise<Task.GetMultilingualVideoListResponse> 多语种视频列表
 */
export const getMultilingualVideoListApi = (
  seriesId: number,
  langCode: string,
  sort: number = 1,
  name?: string
): Promise<Task.GetMultilingualVideoListResponse> => {
  // GET 请求使用 query 参数（params），form-data 参数会作为 URL 查询参数传递
  const params: { seriesId: number; langCode: string; sort: number; name?: string } = {
    seriesId,
    langCode,
    sort,
  };
  if (name && name.trim()) {
    params.name = name.trim();
  }
  return request<Task.GetMultilingualVideoListResponse>('/video/task/list', {
    method: 'GET',
    params,
  });
};

/**
 * 获取缺失任务列表（用于一键导入）
 * 接口：GET /video/task/missing-tasks
 * @param seriesId 剧集ID
 * @param langCode 语言代码
 * @returns Promise<Task.GetMissingTasksResponse> 缺失任务列表
 */
export const getMissingTasksApi = (
  seriesId: number,
  langCode: string
): Promise<Task.GetMissingTasksResponse> => {
  return request<Task.GetMissingTasksResponse>('/video/task/missing-tasks', {
    method: 'GET',
    params: { seriesId, langCode },
  });
};

/**
 * 将用户输入的术语文本转换为转义后的字符串（用于 JSON 的 terminology 字段）
 * @param raw 用户输入的原始文本（可能含换行、双引号等）
 * @returns 转义后的术语字符串，可直接用于 JSON 的 terminology 字段
 * 
 * @example
 * const userInput = `"小子"->"小僧";
 * "hhh"->"aaa22";`;
 * const escaped = toTerminologyJson(userInput);
 * // 返回: "小子\\\"->\\\"小僧\\\";\\n\\\"hhh\\\"->\\\"aaa22\\\";"
 * 
 * // 使用时：
 * const transTerm = {
 *   langCode: "en",
 *   terminology: escaped
 * };
 */
export function toTerminologyJson(raw: string): string {
  // 1. 去掉首尾空白（含换行）
  raw = raw.trim();
  
  // 2. 转义：先转义反斜杠，再转义双引号，最后转义换行/回车
  const escaped = raw
    .replace(/\\/g, '\\\\')   // 先转义已有的反斜杠
    .replace(/"/g, '\\"')     // 再转义双引号
    .replace(/\n/g, '\\n')    // 换行 → \n
    .replace(/\r/g, '\\r');   // 回车 → \r
  
  // 3. 返回转义后的字符串（可直接用于 JSON 的 terminology 字段）
  return escaped;
}

/**
 * 导入任务（一键导入）
 * 接口：POST /video/task/import-tasks
 * @param payload 导入任务请求参数
 * @returns Promise<Task.ImportTasksResponse> 导入任务响应
 */
export const importTasksApi = (
  payload: Task.ImportTasksPayload
): Promise<Task.ImportTasksResponse> => {
  // 处理 transTerms：如果提供了原始术语文本，需要转义
  const processedPayload = { ...payload };
  
  // 如果提供了 transTerms，确保 terminology 字段已转义
  if (processedPayload.transTerms && processedPayload.transTerms.length > 0) {
    processedPayload.transTerms = processedPayload.transTerms.map(term => ({
      ...term,
      // terminology 应该已经是转义后的字符串，但如果需要可以在这里再次确保转义
      terminology: term.terminology
    }));
  }
  
  return request<Task.ImportTasksResponse>('/video/task/import-tasks', {
    method: 'POST',
    body: processedPayload,
  });
};

/**
 * 自动识别字幕
 * 接口：PUT /video/task/auto-subtitle
 * @param payload 自动识别字幕请求参数
 * @returns Promise<Task.AutoSubtitleResponse>
 */
export const autoSubtitleApi = (
  payload: Task.AutoSubtitleParams
): Promise<Task.AutoSubtitleResponse> => {
  return request<Task.AutoSubtitleResponse>('/video/task/auto-subtitle', {
    method: 'PUT',
    body: payload,
  });
};

/**
 * 预估积分
 * 接口：POST /video/task/point-preview
 * - 纯净剧上传等：仅 `{ seriesId }`
 * - 精品译制新增语种：`{ seriesId, langCodes, taskIds, aiVoiceover }`
 * - 一键速译 Tencent MPS 新增语种：`{ seriesId, priceType, targetLangCodes }`
 * - 一键速译 Tencent MPS 创建剧集：`{ priceType, subtitleExtractType, targetLangCodes, videos }`
 */
export const postTaskPointPreviewApi = (body: Task.TaskPointPreviewBody): Promise<Task.TaskPointPreviewResponse> => {
  return request<Task.TaskPointPreviewResponse>("/video/task/point-preview", {
    method: "POST",
    body,
  });
};

/**
 * 批量导出任务资源
 * 接口：POST /video/task/export
 * @param taskIds 要导出资源的 taskId 列表
 */
export const postTaskExportApi = (taskIds: number[]): Promise<Task.TaskExportResponse> => {
  return request<Task.TaskExportResponse>("/video/task/export", {
    method: "POST",
    body: { taskIds },
  });
};

